import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import {
  FaBell,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaCopy,
  FaEnvelope,
  FaExclamationTriangle,
  FaFilter,
  FaMagic,
  FaPaperPlane,
  FaSave,
  FaSearch,
  FaSms,
  FaSyncAlt,
  FaUsers,
  FaWhatsapp,
  FaTimesCircle,
  FaCheck,
  FaLayerGroup,
  FaFileAlt,
  FaBullhorn,
  FaPiggyBank
} from 'react-icons/fa';
import './TemplateAnalise.css';

/* =============================================================================
 * Configurações base
 * ========================================================================== */
const CHANNEL_META = {
  email: {
    label: 'E-mail',
    icon: <FaEnvelope />,
    tone: 'email'
  },
  sms: {
    label: 'SMS',
    icon: <FaSms />,
    tone: 'sms'
  },
  whatsapp: {
    label: 'WhatsApp',
    icon: <FaWhatsapp />,
    tone: 'whatsapp'
  }
};

const TEMPLATE_CATALOG = [
  {
    key: 'weekly_analytics_report',
    label: 'Relatório semanal padrão',
    description: 'Template oficial do digest semanal executado no cron.',
    icon: <FaLayerGroup />,
    isDefaultWeekly: true
  },
  {
    key: 'executive_alert',
    label: 'Alerta executivo',
    description: 'Mensagem curta para exceções relevantes ou alertas pontuais.',
    icon: <FaBullhorn />,
    isDefaultWeekly: false
  },
  {
    key: 'custom_followup',
    label: 'Follow-up analítico',
    description: 'Template livre para envios complementares sob demanda.',
    icon: <FaFileAlt />,
    isDefaultWeekly: false
  }
];

const PLACEHOLDER_GROUPS = [
  {
    title: 'Conta e contato',
    items: [
      { key: 'nome_empresa', desc: 'Nome da empresa / conta no System Hub' },
      { key: 'cliente_id', desc: 'ID interno do cliente' },
      { key: 'responsavel_nome', desc: 'Nome do contato principal / billing' },
      { key: 'responsavel_email', desc: 'E-mail do contato padrão' },
      { key: 'responsavel_telefone', desc: 'Telefone / WhatsApp do contato padrão' }
    ]
  },
  {
    title: 'Período e contexto',
    items: [
      { key: 'periodo', desc: 'Período consolidado do relatório' },
      { key: 'data_inicio', desc: 'Data inicial usada no recorte' },
      { key: 'data_fim', desc: 'Data final usada no recorte' },
      { key: 'modo_envio', desc: 'Cron semanal / envio manual / exceção' },
      { key: 'observacao_envio', desc: 'Observação opcional do disparo forçado' }
    ]
  },
  {
    title: 'Faturamento e auditoria',
    items: [
      { key: 'faturamento_operacional', desc: 'Faturamento operacional consolidado' },
      { key: 'faturamento_realizado', desc: 'Faturamento realizado consolidado' },
      { key: 'gap_auditoria', desc: 'Diferença entre operacional e realizado' },
      { key: 'total_alertas', desc: 'Total de alertas encontrados no período' },
      { key: 'resumo_alertas', desc: 'Resumo textual das anomalias principais' }
    ]
  },
  {
    title: 'Operação',
    items: [
      { key: 'os_fechadas', desc: 'Quantidade total de OS fechadas' },
      { key: 'motoristas_monitorados', desc: 'Quantidade de motoristas no recorte' },
      { key: 'km_total', desc: 'KM total consolidado do período' },
      { key: 'manutencoes_total', desc: 'Total de manutenções associadas' }
    ]
  },
  {
    title: 'Cofre Digital (Banking)',
    icon: <FaPiggyBank />,
    note: 'Agregados apenas — nunca número de cartão ou extrato lançamento a lançamento. Recomendado só para e-mail; use com cautela em SMS/WhatsApp.',
    items: [
      { key: 'banking_status', desc: 'Status da conexão com a Stark Bank (ambiente)' },
      { key: 'banking_saldo_atual', desc: 'Saldo atual da conta digital' },
      { key: 'banking_entradas_periodo', desc: 'Total de entradas no período' },
      { key: 'banking_saidas_periodo', desc: 'Total de saídas no período' },
      { key: 'banking_liquido_periodo', desc: 'Entradas menos saídas no período' },
      { key: 'banking_cartoes_ativos', desc: 'Quantidade de cartões corporativos ativos' },
      { key: 'banking_cobrancas_pix_pendentes', desc: 'Cobranças Pix aguardando pagamento' },
      { key: 'banking_cobrancas_pix_pagas', desc: 'Total já pago em cobranças Pix no período' }
    ]
  },
  {
    title: 'Destaques',
    items: [
      { key: 'top_motorista_nome', desc: 'Motorista destaque do período' },
      { key: 'top_motorista_score', desc: 'Score do motorista destaque' },
      { key: 'top_motorista_os', desc: 'OS do motorista destaque' },
      { key: 'top_motorista_km', desc: 'KM do motorista destaque' }
    ]
  },
  {
    title: 'Links e navegação',
    items: [
      { key: 'link_painel', desc: 'Link direto para o painel Analytics' },
      { key: 'link_templates', desc: 'Link direto para a tela de templates' }
    ]
  }
];

const DEFAULT_TEMPLATES = {
  weekly_analytics_report: {
    email: {
      is_active: true,
      subject: 'Relatório semanal de análises — {{nome_empresa}}',
      body: `
        <h2 style="margin:0 0 12px;">Relatório semanal de análises</h2>
        <p>Olá, <strong>{{responsavel_nome}}</strong>.</p>
        <p>Segue o resumo executivo da conta <strong>{{nome_empresa}}</strong> referente ao período <strong>{{periodo}}</strong>.</p>

        <table style="width:100%; border-collapse:collapse; margin: 12px 0;">
          <tr>
            <td style="padding:8px; border:1px solid #dbe4f0;"><strong>Faturamento operacional</strong></td>
            <td style="padding:8px; border:1px solid #dbe4f0;">{{faturamento_operacional}}</td>
          </tr>
          <tr>
            <td style="padding:8px; border:1px solid #dbe4f0;"><strong>Faturamento realizado</strong></td>
            <td style="padding:8px; border:1px solid #dbe4f0;">{{faturamento_realizado}}</td>
          </tr>
          <tr>
            <td style="padding:8px; border:1px solid #dbe4f0;"><strong>Gap de auditoria</strong></td>
            <td style="padding:8px; border:1px solid #dbe4f0;">{{gap_auditoria}}</td>
          </tr>
          <tr>
            <td style="padding:8px; border:1px solid #dbe4f0;"><strong>OS fechadas</strong></td>
            <td style="padding:8px; border:1px solid #dbe4f0;">{{os_fechadas}}</td>
          </tr>
          <tr>
            <td style="padding:8px; border:1px solid #dbe4f0;"><strong>Motoristas monitorados</strong></td>
            <td style="padding:8px; border:1px solid #dbe4f0;">{{motoristas_monitorados}}</td>
          </tr>
          <tr>
            <td style="padding:8px; border:1px solid #dbe4f0;"><strong>Total de alertas</strong></td>
            <td style="padding:8px; border:1px solid #dbe4f0;">{{total_alertas}}</td>
          </tr>
          <tr>
            <td colspan="2" style="padding:14px 8px 4px; border:none; font-weight:700; color:#0b1f3a;">Situação bancária — {{banking_status}}</td>
          </tr>
          <tr>
            <td style="padding:8px; border:1px solid #dbe4f0;"><strong>Saldo atual</strong></td>
            <td style="padding:8px; border:1px solid #dbe4f0;">{{banking_saldo_atual}}</td>
          </tr>
          <tr>
            <td style="padding:8px; border:1px solid #dbe4f0;"><strong>Entradas no período</strong></td>
            <td style="padding:8px; border:1px solid #dbe4f0;">{{banking_entradas_periodo}}</td>
          </tr>
          <tr>
            <td style="padding:8px; border:1px solid #dbe4f0;"><strong>Saídas no período</strong></td>
            <td style="padding:8px; border:1px solid #dbe4f0;">{{banking_saidas_periodo}}</td>
          </tr>
          <tr>
            <td style="padding:8px; border:1px solid #dbe4f0;"><strong>Cobranças Pix pendentes</strong></td>
            <td style="padding:8px; border:1px solid #dbe4f0;">{{banking_cobrancas_pix_pendentes}}</td>
          </tr>
        </table>

        <p><strong>Resumo de alertas:</strong> {{resumo_alertas}}</p>
        <p><strong>Destaque do período:</strong> {{top_motorista_nome}} — score {{top_motorista_score}}, {{top_motorista_os}} OS e {{top_motorista_km}} km.</p>
        <p><a href="{{link_painel}}">Abrir painel analítico</a></p>
      `.trim()
    },
    sms: {
      is_active: true,
      subject: '',
      body: 'Analytics {{nome_empresa}} | {{periodo}} | Operacional: {{faturamento_operacional}} | Realizado: {{faturamento_realizado}} | Gap: {{gap_auditoria}} | Alertas: {{total_alertas}} | Destaque: {{top_motorista_nome}}.'
    },
    whatsapp: {
      is_active: true,
      subject: '',
      body: '*Relatório semanal de análises*\nConta: *{{nome_empresa}}*\nPeríodo: *{{periodo}}*\nOperacional: *{{faturamento_operacional}}*\nRealizado: *{{faturamento_realizado}}*\nGap: *{{gap_auditoria}}*\nOS fechadas: *{{os_fechadas}}*\nMotoristas monitorados: *{{motoristas_monitorados}}*\nAlertas: *{{total_alertas}}*\nResumo: {{resumo_alertas}}\nDestaque: *{{top_motorista_nome}}* (score {{top_motorista_score}}).\nPainel: {{link_painel}}'
    }
  },
  executive_alert: {
    email: {
      is_active: false,
      subject: 'Alerta executivo — {{nome_empresa}}',
      body: `
        <h2 style="margin:0 0 12px;">Alerta executivo</h2>
        <p>Olá, <strong>{{responsavel_nome}}</strong>.</p>
        <p>Identificamos uma exceção relevante para a conta <strong>{{nome_empresa}}</strong> no período <strong>{{periodo}}</strong>.</p>
        <p><strong>Gap de auditoria:</strong> {{gap_auditoria}}</p>
        <p><strong>Total de alertas:</strong> {{total_alertas}}</p>
        <p><strong>Resumo:</strong> {{resumo_alertas}}</p>
        <p><strong>Observação:</strong> {{observacao_envio}}</p>
        <p><a href="{{link_painel}}">Abrir painel</a></p>
      `.trim()
    },
    sms: {
      is_active: false,
      subject: '',
      body: 'Alerta executivo | {{nome_empresa}} | {{periodo}} | Gap: {{gap_auditoria}} | Alertas: {{total_alertas}} | {{observacao_envio}}'
    },
    whatsapp: {
      is_active: false,
      subject: '',
      body: '*Alerta executivo*\nConta: *{{nome_empresa}}*\nPeríodo: *{{periodo}}*\nGap: *{{gap_auditoria}}*\nAlertas: *{{total_alertas}}*\nObs.: {{observacao_envio}}\nPainel: {{link_painel}}'
    }
  },
  custom_followup: {
    email: {
      is_active: false,
      subject: 'Follow-up analítico — {{nome_empresa}}',
      body: `
        <h2 style="margin:0 0 12px;">Follow-up analítico</h2>
        <p>Olá, <strong>{{responsavel_nome}}</strong>.</p>
        <p>Complementando a visão do período <strong>{{periodo}}</strong>, seguem os principais pontos:</p>
        <ul>
          <li>Operacional: {{faturamento_operacional}}</li>
          <li>Realizado: {{faturamento_realizado}}</li>
          <li>Gap: {{gap_auditoria}}</li>
          <li>OS fechadas: {{os_fechadas}}</li>
        </ul>
        <p><strong>Resumo:</strong> {{observacao_envio}}</p>
        <p><a href="{{link_painel}}">Abrir painel</a></p>
      `.trim()
    },
    sms: {
      is_active: false,
      subject: '',
      body: 'Follow-up {{nome_empresa}} | {{periodo}} | Operacional {{faturamento_operacional}} | Realizado {{faturamento_realizado}} | Obs: {{observacao_envio}}'
    },
    whatsapp: {
      is_active: false,
      subject: '',
      body: '*Follow-up analítico*\nConta: *{{nome_empresa}}*\nPeríodo: *{{periodo}}*\nOperacional: *{{faturamento_operacional}}*\nRealizado: *{{faturamento_realizado}}*\nObs.: {{observacao_envio}}\nPainel: {{link_painel}}'
    }
  }
};

const toInputDate = (date) => {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const today = toInputDate(new Date());
const defaultStart = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return toInputDate(d);
})();

const onlyDigits = (v) => String(v || '').replace(/\D/g, '');

const normalizeTemplatePayload = (raw = {}, templateKey, channel) => {
  const fallback = DEFAULT_TEMPLATES?.[templateKey]?.[channel] || {
    is_active: false,
    subject: '',
    body: ''
  };

  return {
    template_key: templateKey,
    channel,
    is_active: raw.is_active ?? fallback.is_active,
    subject: raw.subject ?? fallback.subject,
    body: raw.body ?? fallback.body,
    updated_at: raw.updated_at || null,
    updated_by_nome: raw.updated_by_nome || '—'
  };
};

const safeArray = (v) => (Array.isArray(v) ? v : []);

/**
 * Normaliza quebras de linha "escapadas": um template mais antigo (ou
 * digitado à mão) pode ter a sequência de texto \n (barra invertida + n)
 * em vez de uma quebra de linha real. No preview de SMS/WhatsApp isso
 * aparecia literalmente na tela, igual apareceria pro destinatário final.
 * Convertemos qualquer variante disso, e também CRLF solto, para quebra de
 * linha de verdade — mesmo tratamento aplicado no backend (renderTemplate
 * do analyticsWeeklyService.js), para o preview local nunca divergir do
 * que realmente sai no envio.
 */
const normalizeLineBreaks = (text) =>
  String(text || '')
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\r\n/g, '\n');

const renderVars = (text, vars = {}) => {
  let out = String(text || '');
  Object.entries(vars).forEach(([key, value]) => {
    out = out.replaceAll(`{{${key}}}`, String(value ?? ''));
  });
  return normalizeLineBreaks(out);
};

const money = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });

const formatPeriodLabel = (ini, fim) => {
  if (!ini || !fim) return 'Período não definido';
  const d1 = new Date(`${ini}T00:00:00`);
  const d2 = new Date(`${fim}T00:00:00`);

  const f = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  return `${f.format(d1)} até ${f.format(d2)}`;
};

export default function TemplateAnalise() {
  const { user, perfil } = useAuth();

  const normalizeRole = (raw) =>
    String(raw || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w]+/g, '_')
      .trim();

  const currentPerfil = normalizeRole(perfil || user?.perfil);
  const isAdminGeral = currentPerfil === 'admin_geral';

  const [activeChannel, setActiveChannel] = useState('email');
  const [activeTemplateKey, setActiveTemplateKey] = useState('weekly_analytics_report');

  const [templates, setTemplates] = useState({});
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [logs, setLogs] = useState([]);
  const [scheduleInfo, setScheduleInfo] = useState({
    cron: '0 4 * * 0',
    timezone: 'America/Sao_Paulo',
    description: 'Todos os domingos às 04:00',
    next_hint: 'Domingo às 04:00',
    last_run_at: null
  });

  const [clients, setClients] = useState([]);
  const [clientContactsMap, setClientContactsMap] = useState({});
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientSearch, setClientSearch] = useState('');

  const [dispatchConfig, setDispatchConfig] = useState({
    dataInicio: defaultStart,
    dataFim: today,
    includeClientIds: [],
    excludeClientIds: [],
    channelsOverride: {
      email: true,
      sms: true,
      whatsapp: true
    },
    usarTemplateSemanalPadrao: true,
    incluirClientesSemContato: false,
    ignorarClientesSuspensos: true,
    observacaoEnvio: '',
    usarSelecaoPositiva: false
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [forcing, setForcing] = useState(false);
  const [feedback, setFeedback] = useState('');

  const currentTemplate = useMemo(() => {
    return (
      templates?.[activeTemplateKey]?.[activeChannel] ||
      normalizeTemplatePayload({}, activeTemplateKey, activeChannel)
    );
  }, [templates, activeTemplateKey, activeChannel]);

  const selectedClient = useMemo(
    () => clients.find((c) => String(c.id) === String(selectedClientId)) || null,
    [clients, selectedClientId]
  );

  const selectedClientContacts = useMemo(
    () => safeArray(clientContactsMap[selectedClientId]),
    [clientContactsMap, selectedClientId]
  );

  const billingContact = useMemo(() => {
    const contacts = selectedClientContacts;
    return contacts.find((c) => c.is_billing) || contacts.find((c) => c.is_principal) || contacts[0] || null;
  }, [selectedClientContacts]);

  const filteredClients = useMemo(() => {
    const term = String(clientSearch || '').toLowerCase().trim();
    if (!term) return clients;

    return clients.filter((c) =>
      String(c.nome_empresa || '').toLowerCase().includes(term)
      || String(c.id).includes(term)
    );
  }, [clients, clientSearch]);

  const availablePlaceholders = useMemo(() => PLACEHOLDER_GROUPS, []);

  const buildSampleVars = useCallback(() => {
    const nomeEmpresa = selectedClient?.nome_empresa || 'Conta Exemplo';
    const clienteId = selectedClient?.id || '000';
    const responsavelNome = billingContact?.nome || 'Responsável Financeiro';
    const responsavelEmail = billingContact?.email || 'financeiro@conta.com';
    const responsavelTelefone = billingContact?.telefone || '(11) 99999-9999';
    const periodo = formatPeriodLabel(dispatchConfig.dataInicio, dispatchConfig.dataFim);

    return {
      nome_empresa: nomeEmpresa,
      cliente_id: clienteId,
      responsavel_nome: responsavelNome,
      responsavel_email: responsavelEmail,
      responsavel_telefone: responsavelTelefone,
      periodo,
      data_inicio: dispatchConfig.dataInicio,
      data_fim: dispatchConfig.dataFim,
      modo_envio: isAdminGeral ? 'Envio manual / cron' : 'Cron semanal',
      observacao_envio: dispatchConfig.observacaoEnvio || 'Sem observações adicionais.',
      faturamento_operacional: money(18250),
      faturamento_realizado: money(17640),
      gap_auditoria: money(610),
      os_fechadas: 38,
      motoristas_monitorados: 9,
      km_total: '1.483,90',
      manutencoes_total: 7,
      total_alertas: 4,
      resumo_alertas: '2 OS sem financeiro, 1 financeiro sem OS e 1 manutenção sem espelho.',
      // 🏦 Cofre Digital — amostra para o preview local (fallback) funcionar
      // sem depender de uma conta Stark Bank real conectada.
      banking_status: 'Conectada (Produção)',
      banking_saldo_atual: money(42380.55),
      banking_entradas_periodo: money(18900),
      banking_saidas_periodo: money(11200),
      banking_liquido_periodo: money(7700),
      banking_cartoes_ativos: 6,
      banking_cobrancas_pix_pendentes: 2,
      banking_cobrancas_pix_pagas: money(15400),
      top_motorista_nome: 'Motorista Destaque',
      top_motorista_score: '87,40',
      top_motorista_os: 11,
      top_motorista_km: '214,30',
      link_painel: `${window.location.origin}/analytics/dados`,
      link_templates: `${window.location.origin}/analytics/templates`
    };
  }, [selectedClient, billingContact, dispatchConfig, isAdminGeral]);

  const refreshPreviewLocally = useCallback(() => {
    const vars = buildSampleVars();
    const subject = renderVars(currentTemplate.subject || '', vars);
    const body = renderVars(currentTemplate.body || '', vars);

    if (activeChannel === 'email') {
      setPreviewHtml(body);
      setPreviewText(subject);
    } else {
      setPreviewHtml('');
      setPreviewText(body);
    }
  }, [activeChannel, currentTemplate, buildSampleVars]);

  const ensureTemplateState = useCallback((incomingTemplates = {}) => {
    const nextState = {};

    TEMPLATE_CATALOG.forEach((tpl) => {
      nextState[tpl.key] = {
        email: normalizeTemplatePayload(incomingTemplates?.[tpl.key]?.email || {}, tpl.key, 'email'),
        sms: normalizeTemplatePayload(incomingTemplates?.[tpl.key]?.sms || {}, tpl.key, 'sms'),
        whatsapp: normalizeTemplatePayload(incomingTemplates?.[tpl.key]?.whatsapp || {}, tpl.key, 'whatsapp')
      };
    });

    return nextState;
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await api.get('/api/system-hub/clientes');
      setClients(safeArray(data));
      if (safeArray(data).length > 0 && !selectedClientId) {
        setSelectedClientId(String(data[0].id));
      }
    } catch (error) {
      console.error(error);
      setFeedback('Falha ao carregar clientes do System Hub.');
    }
  }, [selectedClientId]);

  const fetchClientContacts = useCallback(async (clientId) => {
    if (!clientId || clientContactsMap[clientId]) return;

    try {
      const { data } = await api.get(`/api/system-hub/clientes/${clientId}/contatos`);
      setClientContactsMap((prev) => ({
        ...prev,
        [clientId]: safeArray(data)
      }));
    } catch (error) {
      console.warn('Contatos do cliente não puderam ser carregados:', error?.response?.data || error.message);
      setClientContactsMap((prev) => ({
        ...prev,
        [clientId]: []
      }));
    }
  }, [clientContactsMap]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setFeedback('');
    try {
      const { data } = await api.get('/api/analytics/templates/config');

      const normalized = ensureTemplateState(data?.templates || {});
      setTemplates(normalized);

      if (data?.schedule) {
        setScheduleInfo((prev) => ({ ...prev, ...data.schedule }));
      }

      setLogs(safeArray(data?.logs));

      if (data?.dispatchDefaults) {
        setDispatchConfig((prev) => ({
          ...prev,
          ...data.dispatchDefaults,
          channelsOverride: {
            ...prev.channelsOverride,
            ...(data.dispatchDefaults?.channelsOverride || {})
          }
        }));
      }
    } catch (error) {
      console.error(error);
      setFeedback(error.response?.data?.error || 'Falha ao carregar configuração dos templates.');
      setTemplates(ensureTemplateState({}));
    } finally {
      setLoading(false);
    }
  }, [ensureTemplateState]);

  useEffect(() => {
    loadConfig();
    fetchClients();
  }, [loadConfig, fetchClients]);

  useEffect(() => {
    if (selectedClientId) {
      fetchClientContacts(selectedClientId);
    }
  }, [selectedClientId, fetchClientContacts]);

  useEffect(() => {
    refreshPreviewLocally();
  }, [refreshPreviewLocally]);

  const updateCurrent = (patch) => {
    setTemplates((prev) => ({
      ...prev,
      [activeTemplateKey]: {
        ...prev[activeTemplateKey],
        [activeChannel]: {
          ...prev[activeTemplateKey]?.[activeChannel],
          ...patch
        }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback('');

    try {
      const payload = {
        templateKey: activeTemplateKey,
        is_active: !!currentTemplate.is_active,
        subject: currentTemplate.subject || '',
        body: currentTemplate.body || ''
      };

      await api.put(`/api/analytics/templates/${activeChannel}`, payload);
      setFeedback(`Template "${TEMPLATE_CATALOG.find(t => t.key === activeTemplateKey)?.label}" (${CHANNEL_META[activeChannel].label}) salvo com sucesso.`);
      await loadConfig();
    } catch (error) {
      console.error(error);
      setFeedback(error.response?.data?.error || 'Falha ao salvar template.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setFeedback('');
    try {
      const payload = {
        templateKey: activeTemplateKey,
        channel: activeChannel,
        subject: currentTemplate.subject || '',
        body: currentTemplate.body || '',
        sampleClientId: selectedClientId || null,
        context: {
          ...buildSampleVars()
        }
      };

      const { data } = await api.post('/api/analytics/templates/preview', payload);

      if (activeChannel === 'email') {
        setPreviewHtml(data?.previewHtml || '');
        setPreviewText(data?.previewSubject || '');
      } else {
        setPreviewHtml('');
        setPreviewText(data?.previewText || '');
      }
    } catch (error) {
      console.error(error);
      // fallback local
      refreshPreviewLocally();
      setFeedback('Preview gerado localmente (fallback).');
    }
  };

  const toggleClientInList = (type, clientId) => {
    setDispatchConfig((prev) => {
      const current = new Set(prev[type] || []);
      if (current.has(clientId)) current.delete(clientId);
      else current.add(clientId);

      return {
        ...prev,
        [type]: Array.from(current)
      };
    });
  };

  const handleQuickPeriod = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));

    setDispatchConfig((prev) => ({
      ...prev,
      dataInicio: toInputDate(start),
      dataFim: toInputDate(end)
    }));
  };

  const handleForceSend = async () => {
    if (!isAdminGeral) return;

    setForcing(true);
    setFeedback('');

    try {
      const payload = {
        templateKey: activeTemplateKey,
        dataInicio: dispatchConfig.dataInicio,
        dataFim: dispatchConfig.dataFim,
        includeClientIds: dispatchConfig.usarSelecaoPositiva ? dispatchConfig.includeClientIds : [],
        excludeClientIds: dispatchConfig.excludeClientIds,
        channelsOverride: dispatchConfig.channelsOverride,
        usarTemplateSemanalPadrao: dispatchConfig.usarTemplateSemanalPadrao,
        incluirClientesSemContato: dispatchConfig.incluirClientesSemContato,
        ignorarClientesSuspensos: dispatchConfig.ignorarClientesSuspensos,
        observacaoEnvio: dispatchConfig.observacaoEnvio || '',
        sampleClientId: selectedClientId || null
      };

      const { data } = await api.post('/api/analytics/templates/force-send', payload);
      setFeedback(data?.message || 'Disparo manual executado com sucesso.');
      await loadConfig();
    } catch (error) {
      console.error(error);
      setFeedback(error.response?.data?.error || 'Falha ao forçar o envio do relatório.');
    } finally {
      setForcing(false);
    }
  };

  const copyPlaceholder = async (key) => {
    try {
      await navigator.clipboard.writeText(`{{${key}}}`);
      setFeedback(`Placeholder {{${key}}} copiado.`);
    } catch {
      setFeedback(`Não foi possível copiar {{${key}}}.`);
    }
  };

  const handleUseDefaultWeekly = () => {
    const fallback = DEFAULT_TEMPLATES.weekly_analytics_report?.[activeChannel];
    if (!fallback) return;

    updateCurrent({
      is_active: fallback.is_active,
      subject: fallback.subject,
      body: fallback.body
    });

    setFeedback(`Template ${CHANNEL_META[activeChannel].label} resetado para o padrão semanal.`);
  };

const previewPane = useMemo(() => {
  if (activeChannel === 'email') {
    if (!previewHtml) {
      return <div className="ta-preview-empty">Clique em “Gerar preview” para visualizar o e-mail renderizado.</div>;
    }

    return (
      <div className="ta-preview-mail-wrap">
        <div className="ta-preview-subject">
          <strong>Assunto:</strong> {previewText || currentTemplate.subject || '—'}
        </div>
        <div className="ta-preview-html" dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </div>
    );
  }

  if (activeChannel === 'sms') {
    const smsPreview = String(previewText || '')
      .replace(/\s\|\s/g, '\n')
      .replace(/([.:])\s/g, '$1\n');

    return smsPreview ? (
      <div className="ta-phone-preview ta-phone-preview-sms">
        <div className="ta-phone-shell">
          <div className="ta-phone-notch"></div>
          <div className="ta-phone-header">
            <span>SMS</span>
            <small>Agora</small>
          </div>
          <div className="ta-phone-body">
            <div className="ta-message-bubble ta-message-sms">
              <pre className="ta-preview-message-text">{smsPreview}</pre>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div className="ta-preview-empty">Clique em “Gerar preview” para visualizar a mensagem renderizada.</div>
    );
  }

  if (activeChannel === 'whatsapp') {
    return previewText ? (
      <div className="ta-phone-preview ta-phone-preview-whatsapp">
        <div className="ta-phone-shell">
          <div className="ta-phone-notch"></div>
          <div className="ta-phone-header ta-phone-header-whatsapp">
            <span>WhatsApp</span>
            <small>Agora</small>
          </div>
          <div className="ta-phone-body ta-phone-body-whatsapp">
            <div className="ta-message-bubble ta-message-whatsapp">
              <pre className="ta-preview-message-text">{previewText}</pre>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div className="ta-preview-empty">Clique em “Gerar preview” para visualizar a mensagem renderizada.</div>
    );
  }

  return (
    <div className="ta-preview-empty">Canal não suportado para preview.</div>
  );
}, [activeChannel, previewHtml, previewText, currentTemplate.subject]);
  const selectedTemplateInfo = TEMPLATE_CATALOG.find((t) => t.key === activeTemplateKey);

  return (
    <div className="ta-page">
      <div className="ta-header">
        <div>
          <h1>Template de Análises</h1>
          <p>
            Configure o relatório semanal padrão e mantenha modelos complementares para envios excepcionais por SMS, E-mail e WhatsApp.
          </p>

          <div className="ta-chip-row">
            <span className="ta-chip ta-chip-primary"><FaCalendarAlt /> {scheduleInfo.description}</span>
            <span className="ta-chip"><FaClock /> Fuso: {scheduleInfo.timezone}</span>
            <span className="ta-chip"><FaBell /> Próxima referência: {scheduleInfo.next_hint}</span>
            {scheduleInfo.last_run_at && (
              <span className="ta-chip"><FaCheckCircle /> Último envio: {scheduleInfo.last_run_at}</span>
            )}
          </div>
        </div>

        <div className="ta-header-actions">
          <button className="ta-btn ta-btn-secondary" onClick={loadConfig} disabled={loading}>
            <FaSyncAlt /> {loading ? 'Atualizando...' : 'Atualizar dados'}
          </button>

          <button className="ta-btn ta-btn-ghost" onClick={handlePreview}>
            <FaMagic /> Gerar preview
          </button>

          <button className="ta-btn ta-btn-primary" onClick={handleSave} disabled={saving}>
            <FaSave /> {saving ? 'Salvando...' : 'Salvar template'}
          </button>

          {isAdminGeral && (
            <button className="ta-btn ta-btn-danger" onClick={handleForceSend} disabled={forcing}>
              <FaPaperPlane /> {forcing ? 'Executando...' : 'Rodar agora / emitir relatório'}
            </button>
          )}
        </div>
      </div>

      {feedback && (
        <div className={`ta-feedback ${feedback.toLowerCase().includes('falha') || feedback.toLowerCase().includes('erro') ? 'error' : 'success'}`}>
          {feedback.toLowerCase().includes('falha') || feedback.toLowerCase().includes('erro')
            ? <FaExclamationTriangle />
            : <FaCheckCircle />}
          <span>{feedback}</span>
        </div>
      )}

      <div className="ta-grid ta-grid-top">
        <section className="ta-card ta-card-form">
          <div className="ta-template-catalog">
            {TEMPLATE_CATALOG.map((tpl) => (
              <button
                key={tpl.key}
                className={`ta-template-pill ${activeTemplateKey === tpl.key ? 'active' : ''}`}
                onClick={() => setActiveTemplateKey(tpl.key)}
              >
                {tpl.icon}
                <div>
                  <strong>{tpl.label}</strong>
                  <span>{tpl.description}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="ta-tabs">
            {Object.entries(CHANNEL_META).map(([channel, meta]) => (
              <button
                key={channel}
                className={`ta-tab ${activeChannel === channel ? 'active' : ''} ${meta.tone}`}
                onClick={() => setActiveChannel(channel)}
              >
                {meta.icon}
                <span>{meta.label}</span>
              </button>
            ))}
          </div>

          <div className="ta-form-grid">
            <div className="ta-template-head">
              <div>
                <h3>{selectedTemplateInfo?.label}</h3>
                <p>{selectedTemplateInfo?.description}</p>
              </div>

              {selectedTemplateInfo?.isDefaultWeekly && (
                <span className="ta-badge-default">Padrão do cron semanal</span>
              )}
            </div>

            <label className="ta-switch-row">
              <div>
                <strong>Canal ativo</strong>
                <span>Define se este canal participa do envio para este template.</span>
              </div>
              <input
                type="checkbox"
                checked={!!currentTemplate.is_active}
                onChange={(e) => updateCurrent({ is_active: e.target.checked })}
              />
            </label>

            {activeChannel === 'email' && (
              <label className="ta-field">
                <span>Assunto</span>
                <input
                  type="text"
                  value={currentTemplate.subject || ''}
                  onChange={(e) => updateCurrent({ subject: e.target.value })}
                  placeholder="Assunto do e-mail"
                />
              </label>
            )}

            <label className="ta-field ta-field-textarea">
              <span>Corpo do template</span>
              <textarea
                value={currentTemplate.body || ''}
                onChange={(e) => updateCurrent({ body: e.target.value })}
                placeholder="Escreva aqui o conteúdo do template..."
              />
            </label>

            <div className="ta-actions-inline">
              <button type="button" className="ta-btn ta-btn-light" onClick={handleUseDefaultWeekly}>
                <FaLayerGroup /> Usar template semanal padrão
              </button>

              <span className="ta-meta-inline">
                Última atualização: {currentTemplate.updated_at || '—'} • Responsável: {currentTemplate.updated_by_nome || '—'}
              </span>
            </div>
          </div>
        </section>

        <aside className="ta-card ta-card-side">
          <div className="ta-side-section">
            <h3><FaUsers /> Conta vinculada do System Hub</h3>
            <p>
              O preview e o envio manual podem usar os contatos padrão da gestão de clientes.
            </p>

            <div className="ta-field">
              <span>Buscar conta</span>
              <div className="ta-search-box">
                <FaSearch />
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Buscar por empresa ou ID..."
                />
              </div>
            </div>

            <div className="ta-client-list">
              {filteredClients.map((client) => (
                <button
                  key={client.id}
                  className={`ta-client-item ${String(selectedClientId) === String(client.id) ? 'active' : ''}`}
                  onClick={() => setSelectedClientId(String(client.id))}
                >
                  <div>
                    <strong>{client.nome_empresa}</strong>
                    <span>ID {client.id} • {client.status}</span>
                  </div>
                  {String(selectedClientId) === String(client.id) ? <FaCheck /> : null}
                </button>
              ))}
              {filteredClients.length === 0 && (
                <div className="ta-empty-mini">Nenhuma conta encontrada.</div>
              )}
            </div>

            <div className="ta-contact-box">
              <strong>Contato padrão selecionado</strong>
              {billingContact ? (
                <div className="ta-contact-card">
                  <span><strong>{billingContact.nome}</strong></span>
                  <span>{billingContact.email || 'Sem e-mail'}</span>
                  <span>{billingContact.telefone || 'Sem telefone'}</span>
                </div>
              ) : (
                <div className="ta-empty-mini">
                  Nenhum contato billing/principal carregado para esta conta.
                </div>
              )}
            </div>
          </div>

          <div className="ta-side-section">
            <h3><FaCopy /> Placeholders disponíveis</h3>
            <p>
              Use os campos abaixo para montar o semanal padrão ou mensagens complementares.
            </p>

            {availablePlaceholders.map((group) => (
              <div key={group.title} className="ta-placeholder-group">
                <h4>{group.icon ? <span className="ta-placeholder-group-icon">{group.icon}</span> : null}{group.title}</h4>
                {group.note && <p className="ta-placeholder-group-note">{group.note}</p>}
                <div className="ta-placeholder-list">
                  {group.items.map((item) => (
                    <button key={item.key} className="ta-placeholder" onClick={() => copyPlaceholder(item.key)}>
                      <div>
                        <strong>{`{{${item.key}}}`}</strong>
                        <span>{item.desc}</span>
                      </div>
                      <FaCopy />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>

      <div className="ta-grid ta-grid-middle">
        <section className="ta-card ta-card-dispatch">
          <div className="ta-card-headline">
            <div>
              <h3><FaFilter /> Filtro dinâmico para emissão manual</h3>
              <span>
                Apenas Admin_Geral pode rodar o relatório sob demanda com período, canais e exceções.
              </span>
            </div>
          </div>

          <div className="ta-dispatch-grid">
            <div className="ta-field">
              <span>Data início</span>
              <input
                type="date"
                value={dispatchConfig.dataInicio}
                onChange={(e) => setDispatchConfig((prev) => ({ ...prev, dataInicio: e.target.value }))}
                disabled={!isAdminGeral}
              />
            </div>

            <div className="ta-field">
              <span>Data fim</span>
              <input
                type="date"
                value={dispatchConfig.dataFim}
                onChange={(e) => setDispatchConfig((prev) => ({ ...prev, dataFim: e.target.value }))}
                disabled={!isAdminGeral}
              />
            </div>

            <div className="ta-field ta-field-wide">
              <span>Atalhos de período</span>
              <div className="ta-quick-filters">
                <button type="button" onClick={() => handleQuickPeriod(7)} disabled={!isAdminGeral}>Últimos 7 dias</button>
                <button type="button" onClick={() => handleQuickPeriod(15)} disabled={!isAdminGeral}>Últimos 15 dias</button>
                <button type="button" onClick={() => handleQuickPeriod(30)} disabled={!isAdminGeral}>Últimos 30 dias</button>
              </div>
            </div>

            <label className="ta-switch-row ta-switch-inline">
              <div>
                <strong>Usar seleção positiva</strong>
                <span>Se ativo, envia apenas para as contas escolhidas em “Incluir contas”.</span>
              </div>
              <input
                type="checkbox"
                checked={!!dispatchConfig.usarSelecaoPositiva}
                onChange={(e) => setDispatchConfig((prev) => ({ ...prev, usarSelecaoPositiva: e.target.checked }))}
                disabled={!isAdminGeral}
              />
            </label>

            <label className="ta-switch-row ta-switch-inline">
              <div>
                <strong>Usar template semanal padrão</strong>
                <span>Força o disparo usando o weekly digest oficial, mesmo com outro template selecionado.</span>
              </div>
              <input
                type="checkbox"
                checked={!!dispatchConfig.usarTemplateSemanalPadrao}
                onChange={(e) => setDispatchConfig((prev) => ({ ...prev, usarTemplateSemanalPadrao: e.target.checked }))}
                disabled={!isAdminGeral}
              />
            </label>

            <label className="ta-switch-row ta-switch-inline">
              <div>
                <strong>Ignorar clientes suspensos</strong>
                <span>Evita envio para contas que já estejam suspensas.</span>
              </div>
              <input
                type="checkbox"
                checked={!!dispatchConfig.ignorarClientesSuspensos}
                onChange={(e) => setDispatchConfig((prev) => ({ ...prev, ignorarClientesSuspensos: e.target.checked }))}
                disabled={!isAdminGeral}
              />
            </label>

            <label className="ta-switch-row ta-switch-inline">
              <div>
                <strong>Incluir clientes sem contato padrão</strong>
                <span>Permite tentar envio mesmo sem billing/principal mapeado.</span>
              </div>
              <input
                type="checkbox"
                checked={!!dispatchConfig.incluirClientesSemContato}
                onChange={(e) => setDispatchConfig((prev) => ({ ...prev, incluirClientesSemContato: e.target.checked }))}
                disabled={!isAdminGeral}
              />
            </label>

            <div className="ta-field ta-field-wide">
              <span>Canais do envio manual</span>
              <div className="ta-channel-toggle-row">
                {Object.entries(CHANNEL_META).map(([channel, meta]) => (
                  <button
                    key={channel}
                    type="button"
                    className={`ta-channel-toggle ${dispatchConfig.channelsOverride?.[channel] ? 'active' : ''} ${meta.tone}`}
                    onClick={() => isAdminGeral && setDispatchConfig((prev) => ({
                      ...prev,
                      channelsOverride: {
                        ...prev.channelsOverride,
                        [channel]: !prev.channelsOverride[channel]
                      }
                    }))}
                    disabled={!isAdminGeral}
                  >
                    {meta.icon}
                    <span>{meta.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="ta-field ta-field-wide">
              <span>Incluir contas</span>
              <div className="ta-exception-list">
                {clients.map((client) => {
                  const active = dispatchConfig.includeClientIds.includes(client.id);
                  return (
                    <button
                      key={`include-${client.id}`}
                      type="button"
                      className={`ta-exception-pill ${active ? 'active include' : ''}`}
                      onClick={() => isAdminGeral && toggleClientInList('includeClientIds', client.id)}
                      disabled={!isAdminGeral}
                    >
                      {active ? <FaCheckCircle /> : <FaCheck />}
                      <span>{client.nome_empresa}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="ta-field ta-field-wide">
              <span>Exceções / contas para pular</span>
              <div className="ta-exception-list">
                {clients.map((client) => {
                  const active = dispatchConfig.excludeClientIds.includes(client.id);
                  return (
                    <button
                      key={`exclude-${client.id}`}
                      type="button"
                      className={`ta-exception-pill ${active ? 'active exclude' : ''}`}
                      onClick={() => isAdminGeral && toggleClientInList('excludeClientIds', client.id)}
                      disabled={!isAdminGeral}
                    >
                      {active ? <FaTimesCircle /> : <FaTimesCircle />}
                      <span>{client.nome_empresa}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="ta-field ta-field-wide">
              <span>Observação / exceção do envio</span>
              <textarea
                value={dispatchConfig.observacaoEnvio}
                onChange={(e) => setDispatchConfig((prev) => ({ ...prev, observacaoEnvio: e.target.value }))}
                placeholder="Ex.: disparo manual solicitado pela diretoria para revisar apenas contas premium."
                disabled={!isAdminGeral}
              />
            </div>
          </div>
        </section>
      </div>

      <div className="ta-grid ta-grid-bottom">
        <section className="ta-card ta-card-preview">
          <div className="ta-card-headline">
            <div>
              <h3>Preview do canal</h3>
              <span>{CHANNEL_META[activeChannel].label} • {selectedTemplateInfo?.label}</span>
            </div>
          </div>

          {previewPane}
        </section>

        <section className="ta-card ta-card-logs">
          <div className="ta-card-headline">
            <div>
              <h3>Histórico recente de disparos</h3>
              <span>Últimos envios automáticos e manuais</span>
            </div>
          </div>

          <div className="ta-log-table-wrap">
            <table className="ta-log-table">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Canal</th>
                  <th>Destino</th>
                  <th>Status</th>
                  <th>Conta</th>
                </tr>
              </thead>
              <tbody>
                {(logs || []).map((log) => (
                  <tr key={log.id}>
                    <td>{log.sent_at || log.created_at || '—'}</td>
                    <td>{log.channel}</td>
                    <td>{log.to_email || log.to_phone || '—'}</td>
                    <td>
                      <span className={`ta-status ta-status-${log.status || 'queued'}`}>
                        {log.status || 'queued'}
                      </span>
                    </td>
                    <td>{log.cliente_nome || log.nome_empresa || '—'}</td>
                  </tr>
                ))}
                {(!logs || logs.length === 0) && (
                  <tr>
                    <td colSpan="5" className="ta-empty-row">Nenhum disparo registrado ainda.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}